/**
 * Host → Playjector `processMacroPackageRun` command and reply. The compiled package arrives by
 * reference (a compiled-cache path or a playback asset grant) together with its expected digest.
 */
import { z } from 'zod';
import { macroLiveDetectionSchema, macroRunReplySchema, macroRuntimeModeSchema, playbackAssetGrantSchema,
  playbackErrorCodeSchema, visualTestSensitivitySchema, visualTestPreviewSettingsSchema, type PlaybackErrorCode } from '../macroExecution';
import { sha256HexSchema } from './canonicalJson';
import { COMMON_MACRO_ERROR_CODES, authoringMacroCapabilitiesSchema, commonMacroCapabilitiesSchema,
  hostTargetSchema, hostValidationReceiptSchema } from './pipelineContracts';
import { macroMatchingModeSchema, macroReferenceLoadingModeSchema } from './matchingMode';
import { macroInputDeliverySchema } from './inputDelivery';

const boundedId = z.string().min(1).max(128);
/** Campaign runs use their ready process; editor runs pin the currently selected window/display. */
export const macroPackageCaptureTargetSchema = z.enum(['selected', 'process']);
export type MacroPackageCaptureTarget = z.infer<typeof macroPackageCaptureTargetSchema>;

/** Physical capture pixels occupied by the recorded video, excluding its letterbox bars. */
export const macroMatchingViewportSchema = z.object({
  x: z.number().int().min(0).max(16383), y: z.number().int().min(0).max(16383),
  width: z.number().int().min(1).max(16384), height: z.number().int().min(1).max(16384),
}).strict();
export type MacroMatchingViewport = z.infer<typeof macroMatchingViewportSchema>;

/** Cancel with this run id stops every running session. */
export const MACRO_PACKAGE_CANCEL_ALL_RUN_ID = '*';

export const compiledPackageRefSchema = z.discriminatedUnion('source', [
  z.object({ source: z.literal('path'), directory: z.string().min(3).max(1024) }).strict(),
  z.object({ source: z.literal('grant'), grant: playbackAssetGrantSchema, packageKey: z.string().min(1).max(512) }).strict(),
]);
export type CompiledPackageRef = z.infer<typeof compiledPackageRefSchema>;

const packageStartSchema = z.object({ requestId: boundedId, runId: boundedId, mode: macroRuntimeModeSchema,
    package: compiledPackageRefSchema, packageSha256: sha256HexSchema, liveDetection: macroLiveDetectionSchema.optional(),
    captureTarget: macroPackageCaptureTargetSchema.optional(),
    matchingViewport: macroMatchingViewportSchema.optional(),
    previewSettings: visualTestPreviewSettingsSchema.optional(),
    leaseWaitMs: z.number().int().min(0).max(30_000).optional() }).strict();
export const macroPackageRunCommandSchema = z.discriminatedUnion('command', [
  z.object({ command: z.literal('capabilities'), requestId: boundedId, runId: z.literal('') }).strict(),
  packageStartSchema.extend({ command: z.literal('start') }),
  packageStartSchema.extend({ command: z.literal('prepare') }),
  z.object({ command: z.literal('validate'), requestId: boundedId, runId: boundedId,
    package: compiledPackageRefSchema, packageSha256: sha256HexSchema, expectedHost: hostTargetSchema,
    validationContextDigest: sha256HexSchema, liveDetection: macroLiveDetectionSchema.optional(),
    captureTarget: macroPackageCaptureTargetSchema.optional() }).strict(),
  z.object({ command: z.literal('cancel'), requestId: boundedId, runId: boundedId }).strict(),
  z.object({ command: z.literal('status'), requestId: boundedId, runId: boundedId }).strict(),
  z.object({ command: z.literal('activate'), requestId: boundedId, runId: boundedId,
    matchingViewport: macroMatchingViewportSchema.optional() }).strict(),
  z.object({ command: z.literal('preview-sensitivity'), requestId: boundedId, runId: boundedId,
    sensitivity: visualTestSensitivitySchema, rawScores: z.boolean() }).strict(),
]).superRefine((command, context) => {
  if ((command.command === 'start' || command.command === 'prepare') && command.liveDetection && command.mode !== 'local-shadow') {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Live Detection is observation-only' });
  }
  if ((command.command === 'start' || command.command === 'prepare') && command.matchingViewport && (!command.liveDetection || command.mode !== 'local-shadow')) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'A matching viewport requires an observation run' });
  }
  if ((command.command === 'start' || command.command === 'prepare') && command.previewSettings && !command.liveDetection) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Preview settings require Live Detection', path: ['previewSettings'] });
  }
});
export type MacroPackageRunCommand = z.infer<typeof macroPackageRunCommandSchema>;

export const MACRO_PACKAGE_NEW_ERROR_CODES = [
  'package_not_found', 'package_digest_mismatch', 'package_schema_invalid', 'package_script_invalid',
  'package_ref_digest_mismatch', 'package_too_large', 'native_session_capacity', 'native_session_monitor_conflict',
  'watch_budget_exhausted', 'input_lease_contended', 'input_touch_monitor_unmapped',
  'input_target_unavailable', 'input_target_not_foreground', 'input_target_point_occluded',
  'input_target_check_unavailable', 'input_target_check_failed', 'input_target_focus_timeout',
  'macro_sequence_unsupported', 'macro_graph_migration_required', 'macro_trigger_required', 'trigger_residency_budget_exceeded',
  'trigger_residency_load_timeout', 'trigger_residency_stale',
  ...COMMON_MACRO_ERROR_CODES,
] as const;

type InheritedErrorCode = Exclude<PlaybackErrorCode, `bundle_${string}` | 'native_run_busy_or_invalid'>;
export type MacroPackageErrorCode = InheritedErrorCode | typeof MACRO_PACKAGE_NEW_ERROR_CODES[number];

const inheritedErrorCodes = playbackErrorCodeSchema.options.filter((code): code is InheritedErrorCode =>
  !code.startsWith('bundle_') && code !== 'native_run_busy_or_invalid');

const macroPackageErrorCodes: MacroPackageErrorCode[] = [...inheritedErrorCodes, ...MACRO_PACKAGE_NEW_ERROR_CODES];
export const macroPackageErrorCodeSchema = z.enum(macroPackageErrorCodes as [MacroPackageErrorCode, ...MacroPackageErrorCode[]]);

export const macroPackageNodeEventSchema = z.object({
  kind: z.enum(['node_watching', 'node_matched', 'script_started', 'script_completed', 'node_skipped', 'node_failed']),
  nodeId: z.string().uuid(), qpcUs: z.number().int(), frameQpcUs: z.number().int().optional() }).strict();
export type MacroPackageNodeEvent = z.infer<typeof macroPackageNodeEventSchema>;

const macroNodeIdSchema = z.string().uuid();
const sequenceCountSchema = z.number().int().nonnegative().safe();
export const macroSequencePhaseSchema = z.enum(['loading_triggers', 'waiting_triggers', 'running_inputs',
  'releasing_inputs', 'completed', 'failed', 'cancelled']);
export const macroReferenceResidencyStateSchema = z.enum(['pending', 'ready', 'stale', 'released', 'failed']);
export const macroTriggerProgressSchema = z.object({ macroNodeId: macroNodeIdSchema, regionId: z.string().uuid(),
  state: z.enum(['inactive', 'waiting', 'matched', 'mismatch', 'unavailable']),
  frameSequence: sequenceCountSchema.optional(),
}).strict().superRefine((trigger, context) => {
  if (trigger.state === 'matched' && trigger.frameSequence === undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frameSequence'],
      message: 'A matched Trigger requires its observed frame sequence' });
  }
});
export const macroSequenceProgressSchema = z.object({
  version: z.literal(1), statusSequence: sequenceCountSchema,
  activeMacroNodeId: macroNodeIdSchema.nullable(),
  eligibleMacroNodeIds: z.array(macroNodeIdSchema).max(4096),
  completedMacroNodeIds: z.array(macroNodeIdSchema).max(4096),
  phase: macroSequencePhaseSchema, loadingMode: macroReferenceLoadingModeSchema,
  residency: z.object({ state: macroReferenceResidencyStateSchema, ownerGeneration: sequenceCountSchema,
    deviceEpoch: sequenceCountSchema, requiredReferenceCount: sequenceCountSchema,
    readyReferenceCount: sequenceCountSchema, allocatedBytes: sequenceCountSchema,
    budgetBytes: sequenceCountSchema, loadElapsedUs: sequenceCountSchema,
  }).strict(),
  triggers: z.array(macroTriggerProgressSchema).max(4096),
}).strict().superRefine((progress, context) => {
  const fail = (path: (string | number)[], message: string): void => context.addIssue({
    code: z.ZodIssueCode.custom, path, message,
  });
  if (new Set(progress.eligibleMacroNodeIds).size !== progress.eligibleMacroNodeIds.length) {
    fail(['eligibleMacroNodeIds'], 'Eligible Macro identities must be unique');
  }
  if (new Set(progress.completedMacroNodeIds).size !== progress.completedMacroNodeIds.length) {
    fail(['completedMacroNodeIds'], 'Completed Macro identities must be unique');
  }
  if (progress.activeMacroNodeId && progress.completedMacroNodeIds.includes(progress.activeMacroNodeId)) {
    fail(['activeMacroNodeId'], 'The active Macro cannot already be completed');
  }
  const triggerKeys = progress.triggers.map(trigger => `${trigger.macroNodeId}:${trigger.regionId}`);
  if (new Set(triggerKeys).size !== triggerKeys.length) fail(['triggers'], 'Trigger identities must be unique per Macro');
  const residency = progress.residency;
  if (residency.readyReferenceCount > residency.requiredReferenceCount) {
    fail(['residency', 'readyReferenceCount'], 'Ready Trigger references cannot exceed the requested count');
  }
  if (residency.state === 'ready'
    && (residency.readyReferenceCount !== residency.requiredReferenceCount
      || residency.deviceEpoch === 0 || residency.ownerGeneration === 0)) {
    fail(['residency'], 'GPU-ready Trigger references require complete counts and current generation epochs');
  }
  if (['waiting_triggers', 'running_inputs', 'releasing_inputs'].includes(progress.phase)
    && residency.state !== 'ready') {
    fail(['phase'], 'An armed or running Macro requires GPU-ready Trigger references');
  }
  if (['completed', 'failed', 'cancelled'].includes(progress.phase)
    && (progress.activeMacroNodeId !== null || progress.eligibleMacroNodeIds.length > 0)) {
    fail(['phase'], 'A terminal Macro sequence cannot retain an active or eligible Macro');
  }
});
export type MacroSequenceProgress = z.infer<typeof macroSequenceProgressSchema>;

export const macroPackageRunReplySchema = macroRunReplySchema.extend({
  inputDelivery: macroInputDeliverySchema.optional(),
  ...commonMacroCapabilitiesSchema.partial().shape,
  rawFormatVersions: z.union([commonMacroCapabilitiesSchema.shape.rawFormatVersions,
    authoringMacroCapabilitiesSchema.shape.rawFormatVersions]).optional(),
  compilerVersions: z.union([commonMacroCapabilitiesSchema.shape.compilerVersions,
    authoringMacroCapabilitiesSchema.shape.compilerVersions]).optional(),
  sourceFormatVersions: authoringMacroCapabilitiesSchema.shape.sourceFormatVersions.optional(),
  authoringProfiles: authoringMacroCapabilitiesSchema.shape.authoringProfiles.optional(),
  host: hostTargetSchema.optional(), validationContextDigest: sha256HexSchema.optional(),
  validationReceipt: hostValidationReceiptSchema.optional(),
  errorCode: macroPackageErrorCodeSchema.optional(),
  activeSessions: z.number().int().min(0).max(8).optional(),
  timing: z.object({ p50Us: z.number().int(), p99Us: z.number().int(), maxUs: z.number().int(),
    samples: z.number().int() }).strict().optional(),
  nodeEvent: macroPackageNodeEventSchema.optional(),
  macroSequence: macroSequenceProgressSchema.optional(),
  matchingMode: macroMatchingModeSchema.optional(),
  residentReferenceCount: z.number().int().nonnegative().safe().optional(),
  observedNodeCount: z.number().int().min(0).max(4096).optional(),
}).superRefine((reply, context) => {
  if (reply.outcome === 'validated' && (!reply.validationReceipt || reply.inputsSent || reply.errorCode)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Validated replies require a non-executing Host receipt' });
  }
  if (reply.outcome !== 'validated' && reply.validationReceipt) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['validationReceipt'], message: 'Receipt requires validated outcome' });
  }
  if (reply.outcome === 'capabilities' && (!reply.host || !reply.validationContextDigest || reply.inputsSent || reply.errorCode
    || !((reply.rawFormatVersions?.[0] === 3 || reply.rawFormatVersions?.[0] === 4)
      ? authoringMacroCapabilitiesSchema : commonMacroCapabilitiesSchema).safeParse({
    pipelineVersion: reply.pipelineVersion, rawFormatVersions: reply.rawFormatVersions,
    compiledFormatVersions: reply.compiledFormatVersions, compilerVersions: reply.compilerVersions,
    profile: reply.profile, scoringRule: reply.scoringRule,
    ...(reply.macroSequenceVersion ? { macroSequenceVersion: reply.macroSequenceVersion } : {}),
    ...(reply.sourceFormatVersions ? { sourceFormatVersions: reply.sourceFormatVersions } : {}),
    ...(reply.authoringProfiles ? { authoringProfiles: reply.authoringProfiles } : {}),
  }).success)) context.addIssue({ code: z.ZodIssueCode.custom, message: 'Capabilities require current formats, policy and Host identity' });
});
export type MacroPackageRunReply = z.infer<typeof macroPackageRunReplySchema>;
